import { useState } from "react";
import { Box, Flex, Text, Input, Icon } from "@chakra-ui/react";
import {
  FaBan,
  FaFolder,
  FaCalendarDay,
  FaCalendarWeek,
  FaGift,
  FaCoins,
  FaMapMarkerAlt,
  FaVideo,
  FaBullhorn,
  FaHourglassHalf,
  FaUnlock,
  FaSlidersH,
  FaImage,
  FaTrashAlt,
} from "react-icons/fa";
import OptionSection from "./OptionSection";
import { ServiceKind } from "./ServiceTypeModal";

export type NewServiceData = {
  name: string;
  duration: number;
  maxParticipants: number;
};

type ServiceCreatorFormProps = {
  kind: ServiceKind;
  onCancel: () => void;
  onSave: (data: NewServiceData) => void;
};

const CATEGORY_OPTIONS = [
  { key: "none", icon: FaBan, title: "Nincs kategória", description: "Általános szolgáltatás, nem kerül kategóriába sorolásra." },
  { key: "set", icon: FaFolder, title: "Kategória beállítások", description: "A megadott kategóriában fog megjelenni." },
];

const SCHEDULE_OPTIONS = [
  { key: "custom", icon: FaCalendarDay, title: "Egyéni időpontok", description: "Állíts be személyre szabott időbeosztást." },
  { key: "weekly", icon: FaCalendarWeek, title: "Heti tervezés", description: "Add meg a rendszeresen ismétlődő időpontokat a hét napjaira lebontva." },
];

const PRICE_OPTIONS = [
  { key: "free", icon: FaGift, title: "Ingyenes", description: "Az ár nem kerül megjelenítésre, helyette az „Ingyenes” szöveg lesz látható." },
  { key: "paid", icon: FaCoins, title: "Per alkalom", description: "Az ügyfelek az általad beállított ár alapján fizetnek az alkalomért." },
];

const LOCATION_OPTIONS = [
  { key: "none", icon: FaBan, title: "Nincs", description: "Nem adsz meg helyszínt vagy videókonferencia-linket." },
  { key: "onsite", icon: FaMapMarkerAlt, title: "Helyszín", description: "Adj meg egy helyszínt, ahol ezt a szolgáltatást nyújtod." },
  { key: "online", icon: FaVideo, title: "Videókonferencia", description: "Adj hozzá egy meeting linket, hogy online is kínálhasd." },
];

const WAITLIST_OPTIONS = [
  { key: "none", icon: FaBan, title: "Nincs", description: "Nem lehet várólistára jelentkezni." },
  { key: "instant", icon: FaBullhorn, title: "Azonnali értesítés", description: "Minden várólistás azonnal értesítést kap, ha felszabadul egy hely." },
  { key: "delayed", icon: FaHourglassHalf, title: "Késleltetett értesítés", description: "Az értesítés késleltetve, sorrendben érkezik." },
];

const BOOKING_RULE_OPTIONS = [
  { key: "none", icon: FaUnlock, title: "Nincs megkötés", description: "Az ügyfelek bármikor foglalhatnak és lemondhatják a foglalást." },
  { key: "custom", icon: FaSlidersH, title: "Egyedi beállítások", description: "Beállíthatod a foglalás és lemondás időbeli korlátait." },
];

// Step 2 of "Új szolgáltatás" — the real, fairly long Wobble service form.
// Kept as one scrolling column (matching the real page) but every
// "pick one of N" block is the shared OptionSection, so this file is
// mostly composition, not markup repetition.
const ServiceCreatorForm = ({ kind, onCancel, onSave }: ServiceCreatorFormProps) => {
  const [name, setName] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [duration, setDuration] = useState("50");
  const [breakMin, setBreakMin] = useState("0");
  const [maxParticipants, setMaxParticipants] = useState("6");
  const [category, setCategory] = useState("none");
  const [schedule, setSchedule] = useState("custom");
  const [price, setPrice] = useState("paid");
  const [location, setLocation] = useState("onsite");
  const [waitlist, setWaitlist] = useState("none");
  const [bookingRules, setBookingRules] = useState("none");

  const save = () => {
    onSave({
      name: name.trim() || (kind === "csoportos" ? "Új csoportos óra" : "Új egyéni óra"),
      duration: Number(duration) || 30,
      maxParticipants: kind === "csoportos" ? Number(maxParticipants) || 2 : 1,
    });
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3}>
        <Flex align="center" gap={1.5}>
          <Box as="button" type="button" onClick={onCancel} fontSize="9px" color="slate.500" fontWeight="600">
            ‹ Vissza
          </Box>
        </Flex>
        <Text fontSize="9px" fontWeight="800" color="ink.900" display={{ base: "none", sm: "block" }}>
          Új szolgáltatás
        </Text>
        <Flex gap={1.5}>
          <Box as="button" type="button" onClick={onCancel} fontSize="8px" fontWeight="700" color="wobble.700" border="1px solid" borderColor="wobble.300" borderRadius="full" px={2} py={1}>
            Mégse
          </Box>
          <Box as="button" type="button" onClick={save} fontSize="8px" fontWeight="700" color="white" bg="wobble.600" borderRadius="full" px={2.5} py={1}>
            Mentés
          </Box>
        </Flex>
      </Flex>

      <Box bg="white" border="1px solid" borderColor="chalk.300" borderRadius="lg" p={{ base: 2.5, sm: 3 }} mb={2.5}>
        <Text fontSize="7.5px" fontWeight="700" color="slate.500" mb={0.5}>
          Szolgáltatás típusa
        </Text>
        <Text fontSize="9px" fontWeight="700" color="ink.900">
          {kind === "csoportos" ? "Csoportos foglalás" : "Egyéni foglalás"}
        </Text>
      </Box>

      <Box bg="white" border="1px solid" borderColor="chalk.300" borderRadius="lg" p={{ base: 2.5, sm: 3 }} mb={2.5}>
        <Text fontSize="9px" fontWeight="700" color="ink.900" mb={2}>
          Szolgáltatás részletei
        </Text>
        <Flex gap={2.5} align="flex-start">
          <Box flex="1" minW={0}>
            <Text fontSize="7px" color="slate.500" mb={0.5}>
              Szolgáltatás neve
            </Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kind === "csoportos" ? "pl. Csoportos jóga" : "pl. Személyi edzés"}
              size="xs"
              fontSize="8px"
              borderRadius="md"
              mb={2}
            />
            <Flex justify="space-between" mb={0.5}>
              <Text fontSize="7px" color="slate.500">
                Rövid leírás
              </Text>
              <Text fontSize="7px" color="slate.400">
                {shortDesc.length}/80
              </Text>
            </Flex>
            <Input
              value={shortDesc}
              onChange={(e) => setShortDesc(e.target.value.slice(0, 80))}
              size="xs"
              fontSize="8px"
              borderRadius="md"
            />
          </Box>
          <Flex direction="column" align="center" flexShrink={0}>
            <Flex
              w="46px"
              h="46px"
              borderRadius="md"
              border="1.5px dashed"
              borderColor="chalk.400"
              align="center"
              justify="center"
              direction="column"
              gap={0.5}
              color="slate.400"
            >
              <Icon as={FaImage} boxSize="12px" />
              <Text fontSize="5.5px" textAlign="center" lineHeight="1.2">
                Kép feltöltése
              </Text>
            </Flex>
            <Icon as={FaTrashAlt} boxSize="8px" color="#D64545" mt={1} />
          </Flex>
        </Flex>

        <Flex gap={2} mt={2.5} wrap="wrap">
          <Box flex="1" minW="70px">
            <Text fontSize="7px" color="slate.500" mb={0.5}>
              Időtartam (perc)
            </Text>
            <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} size="xs" fontSize="8px" borderRadius="md" />
          </Box>
          <Box flex="1" minW="60px">
            <Text fontSize="7px" color="slate.500" mb={0.5}>
              Szünet (perc)
            </Text>
            <Input type="number" value={breakMin} onChange={(e) => setBreakMin(e.target.value)} size="xs" fontSize="8px" borderRadius="md" />
          </Box>
          {kind === "csoportos" && (
            <Box flex="1" minW="80px">
              <Text fontSize="7px" color="slate.500" mb={0.5}>
                Max. résztvevők
              </Text>
              <Input type="number" value={maxParticipants} onChange={(e) => setMaxParticipants(e.target.value)} size="xs" fontSize="8px" borderRadius="md" />
            </Box>
          )}
        </Flex>
      </Box>

      <OptionSection title="Kategória beállítások" options={CATEGORY_OPTIONS} selected={category} onSelect={setCategory} />
      <OptionSection title="Időpontok beállítása" options={SCHEDULE_OPTIONS} selected={schedule} onSelect={setSchedule} />
      <OptionSection title="Szolgáltatás ára" options={PRICE_OPTIONS} selected={price} onSelect={setPrice} />
      <OptionSection title="Helyszín" options={LOCATION_OPTIONS} selected={location} onSelect={setLocation} />
      <OptionSection title="Várólista" options={WAITLIST_OPTIONS} selected={waitlist} onSelect={setWaitlist} />
      <OptionSection title="Foglalási beállítások" options={BOOKING_RULE_OPTIONS} selected={bookingRules} onSelect={setBookingRules} />
    </Box>
  );
};

export default ServiceCreatorForm;
